import i18next from 'i18next'
import VueRouter from 'vue-router'

import Book from '@/components/books/Book'
import BookShelf from '@/components/books/BookShelf'
import Chapter from '@/components/books/Chapter'
import ChapterIntro from '@/components/books/ChapterIntro'
import ChapterSection from '@/components/books/ChapterSection'
import TableOfContents from '@/components/books/TableOfContents'

import { AuthStoreNamespacedTypes } from '@/store/auth'
import { BookStoreNamespacedTypes } from '@/store/books'
import { LessonStoreNamespacedTypes } from '@/store/lessons'

import { getIdFromSlug } from '@/utils/string'
import { trackPageView } from '@/utils/google-analytics'
import { isOnStudentSite } from '@/utils/navigation'
import { ChapterSectionType, UserType } from '@/enums'

export const createRouter = store => {
  // Keep track of the first page view to prevent double tracking it in Google Analytics
  let firstPageView = true

  const router = new VueRouter({
    base: '/books/',
    routes: [
      {
        name: 'book-shelf',
        path: '/',
        component: BookShelf,
        meta: { name: i18next.t('book-shelf.title') },
      },
      {
        path: '/:bookSlug',
        component: Book,
        props: true,
        children: [
          {
            name: 'book',
            path: '',
            component: TableOfContents,
            meta: {
              name: route => {
                const bookId = getIdFromSlug(route.params.bookSlug)
                const bookData = store.getters[
                  BookStoreNamespacedTypes.getters.GET_BOOK
                ](bookId)

                return bookData ? bookData.name : '-'
              },
            },
            props: true,
          },
          {
            path: ':chapterSlug',
            component: Chapter,
            props: true,
            children: [
              {
                name: 'book-chapter',
                path: '',
                meta: {
                  name: route => {
                    const chapterId = getIdFromSlug(route.params.chapterSlug)
                    const chapterData = store.getters[
                      BookStoreNamespacedTypes.getters.GET_CHAPTER
                    ](chapterId)

                    return chapterData ? chapterData.name : '-'
                  },
                },
                component: ChapterIntro,
              },
              {
                name: 'book-chapter-section-deprecated',
                path: ':sectionSlug',
                redirect: ({ params }) => ({
                  name: 'book-chapter-section',
                  params: {
                    ...params,
                    sectionTypeString: ChapterSectionType.LESSON.toString(),
                  },
                }),
                props: true,
              },
              {
                name: 'book-chapter-section',
                path: ':sectionTypeString/:sectionSlug',
                component: ChapterSection,
                meta: {
                  name: route => {
                    const lessonId = getIdFromSlug(route.params.sectionSlug)
                    const lessonData = store.getters[
                      LessonStoreNamespacedTypes.getters.GET_LESSON
                    ](lessonId)

                    return lessonData ? lessonData.title : '-'
                  },
                },
                props: true,
              },
            ],
          },
        ],
      },
    ],
    mode: 'history',
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition
      } else {
        // Scroll to the top of the book
        let y = 70

        if (
          store.getters[AuthStoreNamespacedTypes.getters.GET_USER_TYPE]() ===
          UserType.TEACHER
        ) {
          if (isOnStudentSite()) {
            // Teachers viewing the student site needs a little more scrolling to get past the "viewing the student site" message.
            y = 90
          } else {
            // Teachers viewing the teacher site needs a little less scrolling to include the site selector tabs.
            y = 45
          }
        }

        return {
          x: 0,
          y,
        }
      }
    },
  })

  router.afterEach(to => {
    // Only track page views if they're not the first page view
    if (!firstPageView) {
      trackPageView(`/books${to.path}`)
    }

    firstPageView = false
  })

  return router
}
